import { useEffect, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errText, money } from "../../lib/api";
import { Btn, F, PageHeader, Toggle } from "../../components/admin/ui";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { ConfirmDelete } from "./Products";

const EMPTY = { name_ar: "", name_en: "", citiesText: "", fee: 30, free_over: 0, days_min: 2, days_max: 4, active: true, is_default: false };

export default function Shipping() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/shipping").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const { citiesText, id, ...rest } = edit;
    const body = { ...rest, cities: citiesText.split(/[,،\n]/).map((x) => x.trim()).filter(Boolean), fee: Number(rest.fee), free_over: Number(rest.free_over || 0), days_min: Number(rest.days_min), days_max: Number(rest.days_max) };
    try {
      if (id) await api.put(`/admin/shipping/${id}`, body);
      else await api.post("/admin/shipping", body);
      toast.success("تم حفظ منطقة التوصيل");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div data-testid="admin-shipping">
      <PageHeader title="الشحن ومناطق التوصيل" desc="يتم اختيار المنطقة تلقائيًا حسب المدينة. المنطقة الافتراضية تُطبق على أي مدينة غير مدرجة.">
        <Btn data-testid="add-zone-button" onClick={() => setEdit(EMPTY)}><Plus size={16} />منطقة جديدة</Btn>
      </PageHeader>
      <div className="grid gap-4 lg:grid-cols-3">
        {rows.map((z) => (
          <div key={z.id} data-testid={`zone-card-${z.id}`} className="rounded-md border border-gray-200 bg-white p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-bold">{z.name_ar} {z.is_default && <span className="mr-1 rounded bg-gold-soft px-1.5 py-0.5 text-[10px]">افتراضية</span>} {!z.active && <span className="text-xs text-gray-400">(متوقفة)</span>}</p>
                <p className="mt-1 text-2xl font-bold">{money(z.fee)} <span className="text-sm font-normal">ر.س</span></p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEdit({ ...z, citiesText: z.cities.join("، ") })} className="rounded p-1.5 hover:bg-gray-100" data-testid={`edit-zone-${z.id}`}><Pencil size={15} /></button>
                <ConfirmDelete testid={`delete-zone-${z.id}`} onConfirm={async () => { await api.delete(`/admin/shipping/${z.id}`); load(); }} />
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-500">التوصيل خلال {z.days_min}-{z.days_max} أيام · {z.free_over ? `مجاني فوق ${money(z.free_over)} ر.س` : "بدون شحن مجاني"}</p>
            <p className="mt-2 text-xs leading-5 text-gray-600">{z.cities.length ? z.cities.join("، ") : "جميع المدن الأخرى"}</p>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent dir="rtl" className="max-w-lg">
          <DialogTitle>{edit?.id ? "تعديل المنطقة" : "منطقة جديدة"}</DialogTitle>
          {edit && (
            <div className="grid grid-cols-2 gap-3">
              <F label="اسم المنطقة" testid="zone-name-input" value={edit.name_ar} onChange={(v) => setEdit({ ...edit, name_ar: v })} />
              <F label="English" dir="ltr" value={edit.name_en} onChange={(v) => setEdit({ ...edit, name_en: v })} />
              <F label="المدن (افصلي بفاصلة، يمكن إضافة الاسم الإنجليزي)" textarea className="col-span-2" testid="zone-cities-input" value={edit.citiesText} onChange={(v) => setEdit({ ...edit, citiesText: v })} />
              <F label="تكلفة الشحن (ر.س)" type="number" testid="zone-fee-input" value={edit.fee} onChange={(v) => setEdit({ ...edit, fee: v })} />
              <F label="شحن مجاني فوق (0 = لا)" type="number" value={edit.free_over} onChange={(v) => setEdit({ ...edit, free_over: v })} />
              <F label="أقل مدة (أيام)" type="number" value={edit.days_min} onChange={(v) => setEdit({ ...edit, days_min: v })} />
              <F label="أقصى مدة (أيام)" type="number" value={edit.days_max} onChange={(v) => setEdit({ ...edit, days_max: v })} />
              <Toggle label="مفعلة" checked={edit.active} onChange={(v) => setEdit({ ...edit, active: v })} />
              <Toggle label="افتراضية" checked={edit.is_default} onChange={(v) => setEdit({ ...edit, is_default: v })} />
              <Btn data-testid="save-zone-button" className="col-span-2" onClick={save} disabled={!edit.name_ar}>حفظ</Btn>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
